/* eslint-disable require-await */
import { Injectable, effect, inject, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { distinctUntilChanged } from 'rxjs';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

import { Loggable } from '../../../utils/logging/loggable';
import { PRESENTATION_V3_TIMEOUTS, PresentationTransitions, selectors } from '../state/app';

import { PresentationAudioService } from './presentation/presentation-audio.service';

@Injectable({
  providedIn: 'root',
})
export class TransitionPresentationService extends Loggable {
  private store = inject(Store);
  private presentationAudioService = inject(PresentationAudioService);

  public readonly showIconOverlaySignal = signal<boolean>(false);
  public readonly showFullOverlaySignal = signal<boolean>(false);
  public readonly showFadeBackgroundSignal = signal<boolean>(false);
  public readonly showFadeInBackgroundSignal = signal<boolean>(false);
  public readonly overlayPresentationTextSignal = signal<string>('');
  public readonly isAlteredSignal = signal<boolean>(false);
  public readonly isTransitioningSignal = signal<boolean>(false);
  public readonly transitionTimers = signal<PresentationTransitions>(PRESENTATION_V3_TIMEOUTS);

  // Backwards compatibility: expose signal as observable
  // These can be deprecated in the future once all consumers migrate to signals
  public readonly isTransitioning$ = toObservable(this.isTransitioningSignal);

  public constructor() {
    super();
    this.setDisplayName('TransitionPresentationService');

    // Convert the store selector to a signal for the transition timers
    const transitionTimersSignal = toSignal(
      this.store.select(selectors.getTransitionTimers).pipe(distinctUntilChanged()),
      { initialValue: PRESENTATION_V3_TIMEOUTS },
    );

    // Subscribe to changes in transition timers
    effect(() => {
      this.transitionTimers.set(transitionTimersSignal() as PresentationTransitions);
    });
  }

  /**
   * Enable transition mode effect
   *
   * @param {string} overlayText Text displayed in the transition screen (if it will be called), should be member of LoadingTransitionLabel
   */
  public async executeTransitionToPresentation(overlayText: string): Promise<boolean> {
    // We're being called with text: overlayText
    this.isTransitioningSignal.set(true);
    this.overlayPresentationTextSignal.set(overlayText);

    // Start fading audio, no await to fade background at same time
    void this.presentationAudioService.transitionFadeOutAudio(this.transitionTimers().audioFadeTimeout);

    //Fade out background
    await this.enableFadeOutBackground();

    //Fade in Presentation Icon
    await this.enableFadeInTransitionIcon();

    // Enable full transition overlay
    this.isTransitioningSignal.set(true);
    await this.enableFullTransitionOverlay();

    this.presentationAudioService.resetFadedAudioMix();

    this.isTransitioningSignal.set(false);
    this.showFadeBackgroundSignal.set(false);
    this.showIconOverlaySignal.set(false);

    return true;
  }

  public async enableFadeInTransitionIcon(): Promise<boolean> {
    this.showIconOverlaySignal.set(true);

    // Hold transition for some time
    await new Promise((resolve) => {
      setTimeout(async () => {
        resolve(true);
      }, this.transitionTimers().presentationIconFadeIn);
    });
    return true;
  }

  private async enableFullTransitionOverlay(): Promise<boolean> {
    this.showFullOverlaySignal.set(true);

    // Hold transition for some time
    await new Promise((resolve) => {
      setTimeout(async () => {
        this.showFullOverlaySignal.set(false);
        resolve(true);
      }, this.transitionTimers().transitionLoadTimeout);
    });
    return true;
  }

  private async enableFadeOutBackground(): Promise<void> {
    this.showFadeBackgroundSignal.set(true);

    await new Promise((resolve) => {
      setTimeout(async () => {
        this.isAlteredSignal.set(true);
        resolve(true);
      }, this.transitionTimers().backgroundFadeOutTimeout);
    });
  }

  public async enableFadeInBackground(): Promise<void> {
    if (this.isAlteredSignal()) {
      this.showFadeInBackgroundSignal.set(true);

      await new Promise((resolve) => {
        setTimeout(async () => {
          this.showFadeInBackgroundSignal.set(false);
          this.isAlteredSignal.set(false);
          resolve(true);
        }, this.transitionTimers().backgroundFadeInTimeout);
      });
    }
  }

  // Set the overlay text - wrapper around the signal
  public setOverlayPresentationText(text: string): void {
    this.overlayPresentationTextSignal.set(text);
  }
}
